/**
 * Domain Enforcement Tab
 *
 * Shows domain-specific enforcement readiness:
 * - Current vs target policy
 * - Risk simulation (what would happen at stricter policies)
 * - Authentication pass rates
 * - Sources breakdown
 * - Blockers and recommended actions
 * - "Advance Policy" button when ready
 */

import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Sparkles,
  Server,
  Zap,
  ChevronRight,
  ExternalLink,
  HelpCircle,
  RefreshCw,
  ArrowRight,
  Copy,
  Check,
  Loader2,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { api, DomainReadiness, ActionItemsSummary } from "@/lib/api";
import { toast } from "@/lib/toast";

interface EnforcementTabProps {
  domainId: string;
  domainName: string;
  tier?: string;
}

// Policy badge component
function PolicyBadge({ policy, size = "md" }: { policy: string | null; size?: "sm" | "md" }) {
  const colors = {
    reject: "bg-emerald-100 text-emerald-700 border-emerald-200",
    quarantine: "bg-amber-100 text-amber-700 border-amber-200",
    none: "bg-slate-100 text-slate-600 border-slate-200",
  };
  const color = colors[(policy as keyof typeof colors) || "none"];
  const sizeClasses = size === "sm" ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm";

  return (
    <span className={`font-medium rounded-full border ${color} ${sizeClasses}`}>
      p={policy || "none"}
    </span>
  );
}

// Severity indicator
function SeverityBadge({ severity }: { severity: string }) {
  const colors = {
    critical: "bg-red-100 text-red-700",
    high: "bg-orange-100 text-orange-700",
    medium: "bg-amber-100 text-amber-700",
    low: "bg-slate-100 text-slate-600",
  };
  return (
    <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${colors[severity as keyof typeof colors] || colors.low}`}>
      {severity}
    </span>
  );
}

// Progress ring component
function ReadinessRing({ score }: { score: number }) {
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? "#22c55e" : score >= 50 ? "#f59e0b" : "#ef4444";

  return (
    <div className="relative w-28 h-28">
      <svg className="w-28 h-28 -rotate-90">
        <circle
          cx="56"
          cy="56"
          r="40"
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="8"
        />
        <circle
          cx="56"
          cy="56"
          r="40"
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-700"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-slate-800">{score}</span>
        <span className="text-xs text-slate-500">Ready</span>
      </div>
    </div>
  );
}

// Insight type icon (matches dashboard style)
function InsightIcon({ type, severity }: { type: string; severity: string }) {
  const iconClass = "w-4 h-4 flex-shrink-0";
  const colorClass = severity === "critical" ? "text-red-600" :
                     severity === "high" ? "text-orange-500" :
                     severity === "medium" ? "text-amber-500" :
                     "text-blue-500";

  switch (type) {
    case "provider_issue":
      return <Server className={`${iconClass} ${colorClass}`} />;
    case "spoofing_detected":
      return <XCircle className={`${iconClass} ${colorClass}`} />;
    case "authentication_issue":
      return <AlertTriangle className={`${iconClass} ${colorClass}`} />;
    case "forwarder_impact":
      return <Shield className={`${iconClass} text-blue-500`} />;
    default:
      return <Sparkles className={`${iconClass} ${colorClass}`} />;
  }
}

export function EnforcementTab({ domainId, domainName, tier }: EnforcementTabProps) {
  const navigate = useNavigate();
  const [data, setData] = useState<DomainReadiness | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [policyModal, setPolicyModal] = useState<{
    open: boolean;
    targetPolicy: "quarantine" | "reject";
    record?: string;
    hostName?: string;
    loading: boolean;
    verifying: boolean;
    verified?: boolean;
    verifyMessage?: string;
    copied: boolean;
  } | null>(null);

  // Action items for this domain
  const [actionItems, setActionItems] = useState<ActionItemsSummary["items"]>([]);

  async function fetchData() {
    try {
      const response = await api.getEnforcementReadiness();
      const domainData = response.domains.find((d) => d.id === domainId);
      setData(domainData || null);
    } catch (err) {
      console.error("Failed to fetch enforcement data:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  async function fetchActionItems() {
    try {
      const summary = await api.getActionItemsSummary(domainId);
      setActionItems(
        summary.items
          .filter((i) => !i.isCompleted)
          .sort((a, b) => {
            const order: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
            return (order[a.priority] ?? 4) - (order[b.priority] ?? 4);
          })
      );
    } catch (err) {
      console.error("Failed to fetch action items:", err);
    }
  }

  useEffect(() => {
    fetchData();
    if (tier !== "free") {
      fetchActionItems();
    }
  }, [domainId, tier]);

  async function handleRefresh() {
    setRefreshing(true);
    await Promise.all([fetchData(), fetchActionItems()]);
  }

  async function handleAdvancePolicy() {
    if (!data) return;

    const nextPolicy = (data.currentPolicy === "none" ? "quarantine" : "reject") as "quarantine" | "reject";
    setPolicyModal({
      open: true,
      targetPolicy: nextPolicy,
      loading: true,
      verifying: false,
      copied: false,
    });

    try {
      const result = await api.advanceDomainPolicy(domainId, nextPolicy);
      setPolicyModal((prev) =>
        prev ? { ...prev, loading: false, record: result.record, hostName: result.hostName } : null
      );
    } catch (err) {
      console.error("Failed to generate DMARC record:", err);
      toast.error("Failed to generate DMARC record");
      setPolicyModal(null);
    }
  }

  async function handleVerifyDns() {
    if (!policyModal) return;

    setPolicyModal((prev) => prev ? { ...prev, verifying: true, verified: undefined, verifyMessage: undefined } : null);

    try {
      const result = await api.advanceDomainPolicy(domainId, policyModal.targetPolicy, true);
      setPolicyModal((prev) =>
        prev
          ? {
              ...prev,
              verifying: false,
              verified: result.verified,
              verifyMessage: result.verified
                ? `Policy updated to p=${policyModal.targetPolicy}`
                : result.message,
            }
          : null
      );

      if (result.verified) {
        // Refresh enforcement data after short delay
        setTimeout(() => {
          setPolicyModal(null);
          fetchData();
        }, 2000);
      }
    } catch (err) {
      setPolicyModal((prev) =>
        prev
          ? { ...prev, verifying: false, verified: false, verifyMessage: "Failed to verify DNS. Please try again." }
          : null
      );
    }
  }

  async function handleCopyRecord() {
    if (!policyModal?.record) return;
    try {
      await navigator.clipboard.writeText(policyModal.record);
      setPolicyModal((prev) => prev ? { ...prev, copied: true } : null);
      setTimeout(() => setPolicyModal((prev) => prev ? { ...prev, copied: false } : null), 2000);
    } catch {
      // Fallback for clipboard API not available
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <ShieldAlert className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-800 mb-2">
          Enforcement data not available
        </h3>
        <p className="text-slate-500 mb-4">
          We couldn't load enforcement readiness for this domain.
        </p>
        <button
          onClick={handleRefresh}
          className="text-amber-600 hover:text-amber-700 font-medium inline-flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Try Again
        </button>
      </div>
    );
  }

  const isAtFullEnforcement = data.currentPolicy === "reject";
  const hasBlockers = data.blockers.length > 0;
  const criticalBlockers = data.blockers.filter((b) => b.severity === "critical");

  return (
    <div className="space-y-6">
      {/* Header with Refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-amber-500" />
          <h3 className="text-lg font-semibold text-slate-800">Enforcement Readiness</h3>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Readiness Statement Banner */}
      {data.readinessStatement && (
        <div
          className={`rounded-xl p-4 ${
            data.canAdvance
              ? "bg-emerald-50 border border-emerald-200"
              : data.authentication.dmarcPassRate >= 95
                ? "bg-blue-50 border border-blue-200"
                : "bg-amber-50 border border-amber-200"
          }`}
        >
          <p
            className={`text-sm font-medium ${
              data.canAdvance
                ? "text-emerald-700"
                : data.authentication.dmarcPassRate >= 95
                  ? "text-blue-700"
                  : "text-amber-800"
            }`}
          >
            {data.readinessStatement}
          </p>
        </div>
      )}

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Readiness Score & Policy */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex flex-col items-center text-center">
            <ReadinessRing score={data.readinessScore} />

            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-center gap-2">
                <span className="text-sm text-slate-500">Current:</span>
                <PolicyBadge policy={data.currentPolicy} />
              </div>
              {!isAtFullEnforcement && (
                <div className="flex items-center justify-center gap-2">
                  <span className="text-sm text-slate-500">Target:</span>
                  <PolicyBadge policy={data.targetPolicy} />
                </div>
              )}
            </div>

            {/* Advance Policy Button */}
            {!isAtFullEnforcement && (
              data.canAdvance ? (
                <button
                  onClick={handleAdvancePolicy}
                  disabled={!!policyModal}
                  className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  <ShieldCheck className="w-4 h-4" />
                  Advance to p={data.currentPolicy === "none" ? "quarantine" : "reject"}
                </button>
              ) : (
                <div className="mt-4">
                  <button
                    disabled
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 text-slate-400 font-medium rounded-lg cursor-not-allowed"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    Advance to p={data.currentPolicy === "none" ? "quarantine" : "reject"}
                  </button>
                  {data.blockers.length > 0 && (
                    <p className="text-xs text-slate-500 text-center mt-2">
                      {data.blockers.length} blocker{data.blockers.length !== 1 ? "s" : ""} must be resolved first
                    </p>
                  )}
                </div>
              )
            )}

            {isAtFullEnforcement && (
              <div className="mt-4 flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">Full Enforcement</span>
              </div>
            )}
          </div>
        </div>

        {/* Middle Column: Risk Simulation */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-slate-500" />
            Risk Simulation (Last {data.riskSimulation.timeframeDays} Days)
          </h4>

          <div className="space-y-4">
            <div>
              <div className="text-xs text-slate-500 mb-1">Total Messages</div>
              <div className="text-xl font-semibold text-slate-800">
                {data.riskSimulation.totalMessages.toLocaleString()}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-amber-50 rounded-lg">
                <div className="text-xs text-amber-600 mb-1">At p=quarantine</div>
                <div className="text-lg font-semibold text-amber-700">
                  {data.riskSimulation.atQuarantine.percentage.toFixed(1)}%
                </div>
                <div className="text-xs text-amber-600">
                  {data.riskSimulation.atQuarantine.wouldFail.toLocaleString()} would fail
                </div>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <div className="text-xs text-red-600 mb-1">At p=reject</div>
                <div className="text-lg font-semibold text-red-700">
                  {data.riskSimulation.atReject.percentage.toFixed(1)}%
                </div>
                <div className="text-xs text-red-600">
                  {data.riskSimulation.atReject.wouldFail.toLocaleString()} would fail
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Authentication */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h4 className="text-sm font-semibold text-slate-700 mb-4">
            Authentication Pass Rates
          </h4>

          <div className="space-y-4">
            {/* SPF */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-600">SPF</span>
                <div className="flex items-center gap-2">
                  {data.authentication.spfConfigured ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600" />
                  )}
                  <span className={`text-sm font-medium ${
                    data.authentication.spfPassRate >= 95 ? "text-emerald-700" : "text-amber-600"
                  }`}>
                    {data.authentication.spfPassRate}%
                  </span>
                </div>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    data.authentication.spfPassRate >= 95 ? "bg-emerald-500" : "bg-amber-500"
                  }`}
                  style={{ width: `${data.authentication.spfPassRate}%` }}
                />
              </div>
            </div>

            {/* DKIM */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-600">DKIM</span>
                <div className="flex items-center gap-2">
                  {data.authentication.dkimConfigured ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600" />
                  )}
                  <span className={`text-sm font-medium ${
                    data.authentication.dkimPassRate >= 95 ? "text-emerald-700" : "text-amber-600"
                  }`}>
                    {data.authentication.dkimPassRate}%
                  </span>
                </div>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    data.authentication.dkimPassRate >= 95 ? "bg-emerald-500" : "bg-amber-500"
                  }`}
                  style={{ width: `${data.authentication.dkimPassRate}%` }}
                />
              </div>
            </div>

            {/* DMARC */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-600">DMARC</span>
                <span className={`text-sm font-medium ${
                  data.authentication.dmarcPassRate >= 95 ? "text-emerald-700" : "text-amber-600"
                }`}>
                  {data.authentication.dmarcPassRate}%
                </span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    data.authentication.dmarcPassRate >= 95 ? "bg-emerald-500" : "bg-amber-500"
                  }`}
                  style={{ width: `${data.authentication.dmarcPassRate}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sources Summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-slate-700">
            Sending Sources
          </h4>
          <Link
            to={`?tab=sources`}
            className="text-xs text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1"
          >
            Manage Sources
            <ChevronRight className="w-3 h-3" />
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <div className="text-xl font-bold text-slate-800">{data.sources.total}</div>
            <div className="text-xs text-slate-500">Total</div>
          </div>
          <div className="text-center p-3 bg-emerald-50 rounded-lg">
            <div className="text-xl font-bold text-emerald-700">{data.sources.authorized}</div>
            <div className="text-xs text-emerald-700">Authorized</div>
          </div>
          <div className="text-center p-3 bg-amber-50 rounded-lg">
            <div className="text-xl font-bold text-amber-600">{data.sources.pendingReview}</div>
            <div className="text-xs text-amber-600">Pending</div>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <div className="text-xl font-bold text-red-600">{data.sources.unauthorized}</div>
            <div className="text-xs text-red-600">Unauthorized</div>
          </div>
        </div>

        {/* Blocking Sources */}
        {data.sources.blockingSources.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <h5 className="text-xs font-medium text-slate-600 uppercase tracking-wider mb-3">
              Sources Blocking Advancement
            </h5>
            <div className="space-y-2">
              {data.sources.blockingSources.slice(0, 3).map((source) => (
                <div
                  key={source.id}
                  className="flex items-center justify-between p-2 bg-amber-50 rounded-lg"
                >
                  <div>
                    <span className="text-sm font-medium text-slate-800">
                      {source.name || source.providerName || source.identifier}
                    </span>
                    <span className="text-xs text-amber-600 ml-2">{source.issue}</span>
                  </div>
                  <Link
                    to={`/sources/${source.id}`}
                    className="text-xs font-medium text-amber-600 hover:text-amber-800"
                  >
                    {source.fix}
                  </Link>
                </div>
              ))}
              {data.sources.blockingSources.length > 3 && (
                <Link
                  to={`/sources?domain=${domainId}&filter=unauthorized`}
                  className="block text-xs text-amber-600 hover:text-amber-700 font-medium text-center"
                >
                  +{data.sources.blockingSources.length - 3} more blocking sources
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Action Items — domain-specific findings (Dashboard-style compact cards) */}
      {tier === "free" ? (
        <div className="bg-gradient-to-br from-purple-50 to-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h4 className="font-semibold text-slate-800">Action Items</h4>
          </div>
          <p className="text-sm text-slate-600 mb-3">
            Get AI-powered analysis of your domain's email authentication, sender behavior, and security posture.
          </p>
          <button
            onClick={() => navigate("/settings/billing")}
            className="text-sm font-medium text-purple-600 hover:text-purple-700"
          >
            Upgrade to unlock Action Items
          </button>
        </div>
      ) : actionItems.length > 0 ? (
        <div className="bg-gradient-to-br from-purple-50 to-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <h4 className="font-semibold text-slate-800">Action Items</h4>
              <span className="px-1.5 py-0.5 bg-purple-600 text-white text-xs font-medium rounded-full">
                {actionItems.length}
              </span>
            </div>
            <button
              onClick={() => navigate("/alerts")}
              className="text-xs text-purple-600 hover:text-purple-700 font-medium"
            >
              View All
            </button>
          </div>
          <div className="space-y-2">
            {actionItems.map((item) => (
              <button
                key={item.id}
                onClick={() => navigate("/alerts")}
                className="w-full text-left p-3 bg-white rounded-lg border border-purple-100 hover:border-purple-200 hover:shadow-sm transition-all"
              >
                <div className="flex items-start gap-2">
                  <InsightIcon type={item.source} severity={item.priority} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium text-slate-800 text-sm">
                        {item.title}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        item.priority === "critical" ? "bg-red-100 text-red-700" :
                        item.priority === "high" ? "bg-orange-100 text-orange-700" :
                        item.priority === "medium" ? "bg-amber-100 text-amber-700" :
                        item.priority === "low" ? "bg-blue-100 text-blue-700" :
                        "bg-slate-100 text-slate-600"
                      }`}>
                        {item.priority}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2">
                      {item.description}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : hasBlockers ? (
        /* Fallback: generic blockers when no AI insights exist */
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <AlertTriangle className={`w-4 h-4 ${criticalBlockers.length > 0 ? "text-red-600" : "text-amber-500"}`} />
            {data.authentication.dmarcPassRate >= 95 ? "Remaining Items" : "Items to Address"}
            <span className="text-xs font-normal text-slate-400">({data.blockers.length})</span>
          </h4>

          <div className="space-y-3">
            {data.blockers.map((blocker, idx) => {
              const bgColor =
                blocker.severity === "critical" ? "bg-red-50 border-red-100" :
                blocker.severity === "high" ? "bg-orange-50 border-orange-100" :
                blocker.severity === "medium" ? "bg-amber-50 border-amber-100" :
                "bg-slate-50 border-slate-200";
              const iconColor =
                blocker.severity === "critical" ? "text-red-600" :
                blocker.severity === "high" ? "text-orange-600" :
                blocker.severity === "medium" ? "text-amber-600" :
                "text-slate-500";

              return (
                <div
                  key={idx}
                  className={`flex items-start gap-3 p-4 rounded-lg border ${bgColor}`}
                >
                  <AlertTriangle className={`w-4 h-4 ${iconColor} mt-0.5 flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-800">
                        {blocker.title}
                      </span>
                      <SeverityBadge severity={blocker.severity} />
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{blocker.description}</p>
                    {blocker.affectedMessages > 0 && (
                      <p className="text-xs text-slate-500 mt-1">
                        {blocker.affectedMessages.toLocaleString()} messages affected
                      </p>
                    )}
                  </div>
                  {blocker.remediation.link && (
                    <Link
                      to={blocker.remediation.link}
                      className={`text-xs font-medium ${iconColor} hover:opacity-75 flex items-center gap-1 flex-shrink-0`}
                    >
                      Fix
                      <ExternalLink className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Next Step / Recommended Action */}
      {data.recommendedAction && !isAtFullEnforcement && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-800 mb-1">
                {data.recommendedAction.title}
              </h4>
              <p className="text-sm text-slate-600 mb-3">
                {data.recommendedAction.description}
              </p>
              {data.recommendedAction.link && (
                <Link
                  to={data.recommendedAction.link}
                  className="inline-flex items-center gap-2 text-sm font-medium text-amber-700 hover:text-amber-800"
                >
                  Take Action
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Help Link */}
      <div className="flex justify-center">
        <Link
          to="/help#enforcement"
          className="text-sm text-slate-400 hover:text-amber-600 flex items-center gap-1"
        >
          <HelpCircle className="w-4 h-4" />
          Learn more about enforcement
        </Link>
      </div>

      {/* Policy Advancement Modal */}
      <Modal
        open={!!policyModal?.open}
        onOpenChange={(open) => !open && setPolicyModal(null)}
      >
        <Modal.Content
          title={`Advance ${domainName} to p=${policyModal?.targetPolicy ?? ""}`}
          size="lg"
        >
          {/* Modal Body */}
          <div className="p-4 space-y-4">
            {policyModal?.loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-600">
                  Update the DNS TXT record at <code className="px-1.5 py-0.5 bg-slate-100 rounded text-xs font-mono">{policyModal?.hostName}</code> with the following value:
                </p>

                {/* Record Display */}
                <div className="relative">
                  <pre className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm font-mono text-slate-800 whitespace-pre-wrap break-all">
                    {policyModal?.record}
                  </pre>
                  <button
                    onClick={handleCopyRecord}
                    className="absolute top-2 right-2 p-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
                    title="Copy to clipboard"
                  >
                    {policyModal?.copied ? (
                      <Check className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-slate-500" />
                    )}
                  </button>
                </div>

                <p className="text-xs text-slate-500">
                  After updating your DNS record, click "Verify DNS" to confirm the change. DNS propagation may take a few minutes.
                </p>

                {/* Verification Result */}
                {policyModal?.verified !== undefined && (
                  <div
                    className={`p-3 rounded-lg text-sm ${
                      policyModal.verified
                        ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
                        : "bg-amber-50 border border-amber-200 text-amber-700"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {policyModal.verified ? (
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      )}
                      <span>{policyModal.verifyMessage}</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Modal Footer */}
          {policyModal && !policyModal.loading && (
            <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200">
              <button
                onClick={() => setPolicyModal(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
              >
                {policyModal.verified ? "Done" : "Cancel"}
              </button>
              {!policyModal.verified && (
                <button
                  onClick={handleVerifyDns}
                  disabled={policyModal.verifying}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {policyModal.verifying ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Verify DNS
                </button>
              )}
            </div>
          )}
        </Modal.Content>
      </Modal>
    </div>
  );
}